import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// timeout: backend yanıt vermezse istek 20 sn'de iptal edilir. Bu olmadan
// askıdaki bir backend tüm sayfaları sonsuz spinner'da bırakıyor (login
// dahil). Uzun süren işlemler (dosya yükleme vb.) kendi çağrısında
// timeout override edebilir.
const api = axios.create({
    baseURL: `${API_URL}/api`,
    timeout: 20000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = error.config?.url || '';
            const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh');
            const isAlreadyOnLogin = window.location.pathname === '/login';
            if (!isAuthEndpoint && !isAlreadyOnLogin) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    getMe: () => api.get('/auth/me'),
};

// Sections API
export const sectionsAPI = {
    getAll: () => api.get('/sections'),
    getById: (id) => api.get(`/sections/${id}`),
    create: (data) => api.post('/sections', data),
    update: (id, data) => api.put(`/sections/${id}`, data),
    delete: (id) => api.delete(`/sections/${id}`),
};

// Questions API
export const questionsAPI = {
    getBySection: (sectionId) => api.get(`/questions/${sectionId}`),
    getCurrent: (sectionId) => api.get(`/questions/${sectionId}/current`),
    create: (data) => api.post('/questions', data),
    update: (id, data) => api.put(`/questions/${id}`, data),
    delete: (id) => api.delete(`/questions/${id}`),
};

// Certificates API
export const certificatesAPI = {
    generate: () => api.post('/certificates/generate'),
    verify: (certNumber) => api.get(`/certificates/verify/${certNumber}`),
    getMy: () => api.get('/certificates/my'),
};

// Auth Mode API
export const authModeAPI = {
    getMode: () => api.get('/auth/mode'),
};

// Public API (no auth required)
export const publicAPI = {
    getStats: () => api.get('/public/stats'),
};

// Admin API
export const adminAPI = {
    getUsers: () => api.get('/admin/users'),
    getUserProgress: (userId) => api.get(`/admin/users/${userId}/progress`),
    getStats: () => api.get('/admin/stats'),
    getQuestions: () => api.get('/admin/questions'),
    getSections: () => api.get('/admin/sections'),
    importContent: (data) => api.post('/import/content', data),
    updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
    updateUserDepartment: (userId, department) => api.put(`/admin/users/${userId}/department?department=${department}`),
};

// Tenant Admin User Management — bir tenant'ın kendi kullanıcıları üzerinde
// rol/departman/öğretmen/aktif değişiklikleri yapar (super_admin koruması var).
export const tenantUsersAPI = {
    list: () => api.get('/tenant-admin/users'),
    setRole: (userId, role) => api.put(`/tenant-admin/users/${userId}/role`, { role }),
    setDepartment: (userId, department) => api.put(`/tenant-admin/users/${userId}/department`, { department }),
    setTrainee: (userId, isTrainee) => api.put(`/tenant-admin/users/${userId}/trainee`, { is_trainee: isTrainee }),
    setInstructor: (userId, isInstructor, moduleIds) => api.put(`/tenant-admin/users/${userId}/instructor`, {
        is_instructor: isInstructor,
        instructor_module_ids: moduleIds || [],
    }),
    setActive: (userId, isActive) => api.put(`/tenant-admin/users/${userId}/active`, { is_active: isActive }),
    resetPassword: (userId) => api.put(`/tenant-admin/users/${userId}/reset-password`),
};

// Mevzuat (Legislation) API
export const mevzuatAPI = {
    getLaws: () => api.get('/mevzuat/laws'),
    getLaw: (lawId) => api.get(`/mevzuat/laws/${lawId}`),
    getArticles: (lawId = null) => api.get('/mevzuat/articles' + (lawId ? `?law_id=${lawId}` : '')),
    getArticle: (lawId, articleNo) => api.get(`/mevzuat/laws/${lawId}/articles/${articleNo}`),
    getArticleDetail: (articleId) => api.get(`/mevzuat/articles/${articleId}`),
    getByRefs: (refs) => api.get(`/mevzuat/by-refs?refs=${refs.join(',')}`),
};

// Modules API
export const modulesAPI = {
    getAll: () => api.get('/modules'),
    getById: (id) => api.get(`/modules/${id}`),
    getTrainingContent: (moduleId) => api.get(`/training-content/${moduleId}`),
};

// Topics API
export const topicsAPI = {
    getAll: () => api.get('/topics'),
    getByModule: (moduleId) => api.get(`/topics/by-module/${moduleId}`),
    getById: (topicId) => api.get(`/topics/${topicId}`),
    getLessonsWithProgress: (topicId) => api.get(`/topics/${topicId}/lessons-with-progress`),
};

// Lessons API
export const lessonsAPI = {
    getAll: () => api.get('/lessons'),
    getByTopic: (topicId) => api.get(`/lessons/by-topic/${topicId}`),
    getById: (lessonId) => api.get(`/lessons/${lessonId}`),
    getQuestions: (lessonId) => api.get(`/lessons/${lessonId}/questions`),
    canAccess: (lessonId) => api.get(`/lessons/${lessonId}/can-access`),
};

// Progress API
export const progressAPI = {
    // Legacy section progress
    submitAnswer: (sectionId, data) => api.post(`/progress/answer?section_id=${sectionId}`, data),
    getOverview: () => api.get('/progress/overview'),
    reset: (sectionId) => api.post(`/progress/reset/${sectionId}`),
    
    // Lesson progress (new)
    getAllLessons: () => api.get('/progress/lessons'),
    getLesson: (lessonId) => api.get(`/progress/lessons/${lessonId}`),
    getLessonsByTopic: (topicId) => api.get(`/progress/lessons/by-topic/${topicId}`),
    updateContent: (lessonId, data) => api.post(`/progress/lessons/${lessonId}/content`, data),
    updateQuiz: (lessonId, data) => api.post(`/progress/lessons/${lessonId}/quiz`, data),
    
    // Topic & Module progress
    getTopic: (topicId) => api.get(`/progress/topics/${topicId}`),
    getModule: (moduleId) => api.get(`/progress/modules/${moduleId}`),
    getAllModules: () => api.get('/progress/modules-batch'),
};

// Reports API
export const reportsAPI = {
    getOverview: () => api.get('/reports/overview'),
    getUserReport: (userId) => api.get(`/reports/user/${userId}`),
    getAuditTrail: (params) => api.get('/reports/audit-trail', { params }),
    getQuizAttempts: (lessonId) => api.get(`/reports/quiz-attempts/${lessonId}`),
};

// Weak Topics API (Zayıf Konular)
export const weakTopicsAPI = {
    getAll: () => api.get('/user/weak-topics'),
    getSummary: () => api.get('/user/weak-topics/summary'),
    save: (data) => api.post('/user/weak-topics/save', data),
    markReviewed: (topicId) => api.post(`/user/weak-topics/${topicId}/mark-reviewed`),
    delete: (topicId) => api.delete(`/user/weak-topics/${topicId}`),
};

// Content Updates API (İçerik Güncellemeleri)
export const contentUpdatesAPI = {
    getAll: () => api.get('/user/content-updates'),
    getSummary: () => api.get('/user/content-updates/summary'),
    acknowledge: (lessonId) => api.post(`/user/content-updates/${lessonId}/acknowledge`),
    getTopicUpdateInfo: (topicId) => api.get(`/topics/${topicId}/update-info`),
};

// Exam Types API
export const examTypesAPI = {
    getAll: () => api.get('/exam-types'),
};

// Departments API
export const departmentsAPI = {
    getAll: () => api.get('/departments'),
    getAgentTypes: () => api.get('/agent-types'),
};

// Auditor API (Denetim Paneli)
export const auditorAPI = {
    getDashboard: () => api.get('/auditor/dashboard'),
    getComplianceSummary: () => api.get('/auditor/compliance-summary'),
    getNonCompliantUsers: (department = null, limit = 50) => {
        const params = new URLSearchParams();
        if (department) params.append('department', department);
        params.append('limit', limit);
        return api.get(`/auditor/non-compliant-users?${params.toString()}`);
    },
    getOverdueTrainings: (daysThreshold = 30) => api.get(`/auditor/overdue-trainings?days_threshold=${daysThreshold}`),
    getUserDetail: (userId) => api.get(`/auditor/user-detail/${userId}`),
    exportReport: (format = 'json') => api.get(`/auditor/export/compliance-report?format=${format}`),
    getCompanySettings: () => api.get('/auditor/company-settings'),
    getAllUsersProgress: () => api.get('/auditor/all-users-progress'),
    getTrainingProofReport: (userId) => api.get(`/audit/training-proof-report/${userId}`),
};

// Gamification & UX API
export const gamificationAPI = {
    getUserStats: () => api.get('/user/stats'),
    addXP: (xpAmount = 100, lessonId = null) => {
        const params = new URLSearchParams();
        params.append('xp_amount', xpAmount);
        if (lessonId) params.append('lesson_id', lessonId);
        return api.post(`/user/add-xp?${params.toString()}`);
    },
    updateDailyGoal: (goal) => api.put(`/user/daily-goal?goal=${goal}`),
    getContinueLearning: () => api.get('/user/continue-learning'),
    getNextLesson: (lessonId) => api.get(`/user/next-lesson/${lessonId}`),
    getLeaderboard: (scope = 'all', limit = 10) => api.get(`/leaderboard?scope=${scope}&limit=${limit}`),
};

// Dashboard API - Birleşik endpoint (performans optimizasyonu)
export const dashboardAPI = {
    getSummary: () => api.get('/dashboard/summary'),
};

// Onboarding API
export const onboardingAPI = {
    getStatus: () => api.get('/onboarding/status'),
    complete: (data) => api.post('/onboarding/complete', data),
    skip: () => api.post('/onboarding/skip'),
};

// Badges/Achievements API
export const badgesAPI = {
    getAll: () => api.get('/badges/all'),
    getMy: () => api.get('/badges/my'),
    getNew: () => api.get('/badges/new'),
    markSeen: () => api.post('/badges/mark-seen'),
};

// Bulk Import API
export const bulkImportAPI = {
    uploadUsers: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/admin/users/bulk-import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
    },
    getTemplate: () => api.get('/admin/users/bulk-import/template', { responseType: 'blob' }),
};

export default api;
